import { FormEvent, useMemo, useState } from 'react';

import { LiquidityDeselectedAlert } from '@/components/TransactionModule/Alerts/LiquidityDeselectedAlert';

import { CHAIN_IDS } from '@/constants/chain';

import { Source, useLiquiditySourcesStore } from '@/store/liquiditySources';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { SearchIcon } from '@/ui/Icons/SearchIcon';
import { Tooltip } from '@/ui/Tooltip';

import { getAvailableRfqLikeSources } from '@/utils/liquiditySourcesValidation';

import { LiquiditySourcesTable } from './LiquiditySourcesTable';

export interface SourceToRender extends Source {
  /** Key of a liquidity source. */
  key: string;
}

interface LiquiditySourcesProps {
  /** ID of the current network. */
  chainId: number;
  /** The callback to stage new LS ids */
  onSourcesSelected: (ids: string[]) => void;
  /** The currently staged IDS */
  stagedSources: string[] | null;
}

/**
 * A settings menu to configure which exchanges a trade sources its liquidity from.
 */
export const LiquiditySources = ({
  chainId,
  onSourcesSelected,
  stagedSources,
}: LiquiditySourcesProps): React.ReactElement | null => {
  // global state
  const { selected, sources, total } = useLiquiditySourcesStore(
    (state) => state.liquiditySources?.[chainId],
  );

  // local state
  const [searchQuery, setSearchQuery] = useState<string>('');

  // vars
  const buttonText = 'Reset defaults';

  const sourceKeys = useMemo(() => Object.keys(sources), [sources]);
  const enabledSourceKeys = useMemo(
    () => sourceKeys.filter((key) => sources[key].enabled),
    [sourceKeys, sources],
  );

  // Memoized so its identity stays stable across selection toggles, keeping the
  // table's row model intact (only re-rendering, not rebuilding, on each toggle).
  const sourcesToRender = useMemo<SourceToRender[]>(() => {
    const query = searchQuery.trim().toLowerCase();
    return sourceKeys
      .filter((key) =>
        // if there is a search query, test it against the source name - otherwise return all sources
        query ? sources[key].displayName.toLowerCase().includes(query) : true,
      )
      .map((key) => ({ key, ...sources[key] }));
  }, [sourceKeys, sources, searchQuery]);

  // Calculate which keys should be disabled
  const currentlySelected = stagedSources || enabledSourceKeys;

  const rfqLikeSourcesSet = useMemo(
    () => new Set(getAvailableRfqLikeSources(sourceKeys)),
    [sourceKeys],
  );
  const rfqLikeSources = useMemo(() => [...rfqLikeSourcesSet], [rfqLikeSourcesSet]);
  const nonRfqLikeSources = sourceKeys.filter((key) => !rfqLikeSourcesSet.has(key));
  const allNonRfqLikeSourcesSelected = nonRfqLikeSources.every((source) =>
    currentlySelected.includes(source),
  );

  // RFQ-like sources should be disabled if not all non-RFQ-like sources are selected
  const disabledKeys = allNonRfqLikeSourcesSelected ? [] : rfqLikeSources;

  const handleReset = () => {
    onSourcesSelected(sourceKeys);
  };

  // render
  if (chainId === CHAIN_IDS.SOLANA) return null;
  if (!sourceKeys.length) return null;

  return (
    <Tooltip.Provider>
      <div className="max-h-[550px] tablet:max-h-full overflow-y-auto px-4 tablet:px-6 pb-8">
        <LiquidityDeselectedAlert className="mb-3" />
        <div className="bg-token-gray-300 flex items-center mb-4 rounded-sm h-10">
          <SearchIcon className="text-token-gray-1600 h-4 px-2" />

          <input
            autoFocus
            type="text"
            className={fontStyles({
              variant: 'BODY_MD',
              class:
                'bg-token-gray-300 w-full border-0 placeholder:text-token-gray-800 focus:outline-none',
            })}
            onChange={(e: FormEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value)}
            placeholder="Search liquidity source"
            value={searchQuery}
          />
        </div>

        {!!sourcesToRender.length ? (
          <LiquiditySourcesTable
            sources={sourcesToRender}
            selectedKeys={currentlySelected}
            disabledKeys={disabledKeys}
            rfqLikeSourcesSet={rfqLikeSourcesSet}
            sourceKeys={sourceKeys}
            onSourcesSelected={onSourcesSelected}
          />
        ) : (
          <p
            className={fontStyles({
              variant: 'BODY_LG',
              class: 'text-token-gray-800 py-12 text-center',
            })}
          >
            No sources found.
          </p>
        )}
      </div>

      <div className="items-center flex space-between p-4 static bottom-0 left-0 right-0 z-10 border-t-1 border-special-border-divider">
        <button
          className={buttons({
            color: 'transparent',
            shape: 'rounded',
            size: 'small',
            class: 'px-5',
          })}
          onClick={handleReset}
          disabled={stagedSources === null ? selected === total : stagedSources.length === total}
        >
          {buttonText}
        </button>
      </div>
    </Tooltip.Provider>
  );
};
